import type { FunctionInvokeOptions } from "@supabase/supabase-js";
import {
	artistSupabase,
	currentSupabaseSession,
	readArtistSupabaseConfiguration,
} from "./client";

export class ArtistEdgeFunctionError extends Error {
	readonly status: number | null;
	readonly payload: unknown;

	constructor(name: string, status: number | null, payload: unknown) {
		super(`${name} failed${status === null ? "" : ` (${status})`}`);
		this.name = "ArtistEdgeFunctionError";
		this.status = status;
		this.payload = payload;
	}
}

export async function invokeArtistFunction<T>(
	name: string,
	options: FunctionInvokeOptions = {},
): Promise<T> {
	const { data, error } = await artistSupabase().functions.invoke<T>(name, options);
	if (error) {
		const context = "context" in error ? error.context : undefined;
		let payload: unknown = null;
		let status: number | null = null;
		if (context instanceof Response) {
			status = context.status;
			payload = await context.clone().json().catch(async () => context.text());
		}
		throw new ArtistEdgeFunctionError(name, status, payload);
	}
	if (data === null) {
		throw new ArtistEdgeFunctionError(name, null, null);
	}
	return data;
}

export async function invokeArtistRoute<T>(
	path: `/${string}`,
	options: Readonly<{
		method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
		body?: BodyInit | null;
		headers?: HeadersInit;
		timeoutMilliseconds?: number;
	}> = {},
): Promise<T> {
	const configuration = readArtistSupabaseConfiguration();
	const session = await currentSupabaseSession();
	const headers = new Headers(options.headers);
	headers.set("apikey", configuration.publishableKey);
	headers.set(
		"Authorization",
		`Bearer ${session?.access_token ?? configuration.publishableKey}`,
	);
	if (
		options.body !== undefined &&
		options.body !== null &&
		!(options.body instanceof FormData) &&
		!headers.has("Content-Type")
	) {
		headers.set("Content-Type", "application/json");
	}
	const requestInit: RequestInit = {
		method: options.method ?? "POST",
		headers,
		cache: "no-store",
		redirect: "error",
		signal: AbortSignal.timeout(options.timeoutMilliseconds ?? 30_000),
		...(options.body === undefined ? {} : { body: options.body }),
	};
	const response = await fetch(
		`${configuration.url}/functions/v1/artist-api${path}`,
		requestInit,
	);
	const payload: unknown = await response.clone().json().catch(async () => {
		const text = await response.text();
		return text.length === 0 ? null : text;
	});
	if (!response.ok) {
		throw new ArtistEdgeFunctionError("artist-api", response.status, payload);
	}
	return payload as T;
}
